import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@radix-ui/react-label";
import { Button } from "@/components/ui/button";
import { ImageCropModal } from "@/components/common/image-crop-modal";
import { Image } from '@/components/common/image';
import { Spinner } from "@/components/ui/modal";
import { ReplaceIcon } from "./assets";
import { cn } from "@/lib/utils";

interface AvatarUploadProps {
  avatar?: string;
  title: string;
  uploadClassName?: string;
  onUpload: (file: File) => Promise<void>;
}

export function AvatarUpload({ avatar, title, uploadClassName, onUpload }: AvatarUploadProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(avatar);

  const handleUpload = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png, image/jpeg";
    input.multiple = false;
    input.size = 1024 * 1024 * 1024;
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        setAvatarUrl(URL.createObjectURL(file));
        setCropModalOpen(true);
      }
    };
    input.click();
  };

  const handleCropComplete = async (blob: Blob) => {
    setIsLoading(true);
    setCropModalOpen(false);
    setAvatarUrl("");
    await onUpload(new File([blob], "avatar.png", { type: "image/png" }));
    setIsLoading(false);
  };

  return (
    <div>
      <Label className="block mb-3 text-sm font-medium text-i-normal">
        {t("settings_account.avatar_upload.avatar")}
      </Label>
      <div className="flex items-start">
        <div
          className="group relative mr-5 w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xl font-semibold overflow-hidden cursor-pointer"
          onClick={handleUpload}
        >
          {avatar ? (
            <Image
              src={avatar}
              alt={title}
              className="w-full h-full object-cover"
            />
          ) : (
            <span>{title?.charAt(0)}</span>
          )}
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-[linear-gradient(rgba(0,0,0,0.4),rgba(0,0,0,0.4))]">
              <Spinner />
            </div>
          ) : (
            <div className="absolute invisible group-hover:visible inset-0 flex items-center justify-center bg-[linear-gradient(rgba(0,0,0,0.4),rgba(0,0,0,0.4))]">
              <ReplaceIcon className="w-5 h-5" />
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <div className="mb-1 text-sm font-medium text-[rgb(10,13,20)]">
            {t("settings_account.avatar_upload.upload_image")}
          </div>
          <div className="mb-3 text-xs text-[rgba(82,88,102,1)]">
            {t("settings_account.avatar_upload.image_requirements")}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleUpload}
            className={cn(
              "p-1.5 rounded-sm w-fit text-sm text-i-normal font-medium border-[rgba(208,213,221,1)] hover:border-[rgba(102,112,133,1)] bg-i-white hover:bg-i-white shadow-[0px_1px_2px_rgba(16,24,40,0.05)]",
              uploadClassName
            )}
          >
            {t("settings_account.avatar_upload.upload")}
          </Button>
        </div>
      </div>
      <ImageCropModal
        open={cropModalOpen}
        imageUrl={avatarUrl}
        onOpenChange={() => {
          setCropModalOpen(!cropModalOpen);
        }}
        onCropComplete={handleCropComplete}
      />
    </div>
  );
}